/**
 * OverlayView Component
 * Overlay chart view for comparing asset trends
 */

import { useMemo, useState } from 'react'
import { useComparisonStore } from '@/store'
import { Card } from '@/components/common'
import { TimeSeriesChart, HeatmapChart } from '@/components/charts'
import { AVAILABLE_KPIS } from '@/types/comparison'
import clsx from 'clsx'

// Color palette for assets
const ASSET_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
]

type MetricKey = 'reliability' | 'health_score' | 'anomaly_score'

export default function OverlayView() {
  const { kpiComparison, trendComparison, correlationData } = useComparisonStore()
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>('reliability')

  const metrics: Array<{ id: MetricKey; label: string }> = [
    { id: 'reliability', label: 'Reliability' },
    { id: 'health_score', label: 'Health Score' },
    { id: 'anomaly_score', label: 'Anomaly Score' }
  ]

  // Prepare overlay trend data
  const overlayData = useMemo(() => {
    if (!trendComparison?.trends) return []

    return Object.entries(trendComparison.trends).map(([assetId, metrics], index) => ({
      x: trendComparison.timestamps,
      y: metrics[selectedMetric] || [],
      name: assetId,
      color: ASSET_COLORS[index % ASSET_COLORS.length]
    }))
  }, [trendComparison, selectedMetric])

  // Prepare correlation heatmap
  const correlationHeatmap = useMemo(() => {
    if (!correlationData?.matrix) return null

    return {
      data: correlationData.matrix,
      labels: correlationData.kpis.map(kpiId => {
        const kpi = AVAILABLE_KPIS.find(k => k.id === kpiId)
        return kpi?.label || kpiId
      })
    }
  }, [correlationData])

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!kpiComparison?.assets) return null

    const reliabilities = kpiComparison.assets.map(a => a.metrics.reliability)
    const healthScores = kpiComparison.assets.map(a => a.metrics.health_score)

    return {
      reliabilityRange: {
        min: Math.min(...reliabilities).toFixed(1),
        max: Math.max(...reliabilities).toFixed(1),
        spread: (Math.max(...reliabilities) - Math.min(...reliabilities)).toFixed(1)
      },
      healthRange: {
        min: Math.min(...healthScores).toFixed(0),
        max: Math.max(...healthScores).toFixed(0),
        spread: (Math.max(...healthScores) - Math.min(...healthScores)).toFixed(0)
      }
    }
  }, [kpiComparison])

  if (!kpiComparison || kpiComparison.assets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-surface-400">
        Select assets to see overlay comparison
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Metric Selector */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-surface-400">Metric:</span>
        <div className="flex bg-surface-800 rounded-lg p-1">
          {metrics.map((metric) => (
            <button
              key={metric.id}
              onClick={() => setSelectedMetric(metric.id)}
              className={clsx(
                'px-3 py-1.5 rounded text-sm transition-colors',
                selectedMetric === metric.id
                  ? 'bg-primary-500/20 text-primary-400'
                  : 'text-surface-400 hover:text-surface-200'
              )}
            >
              {metric.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Main Overlay Chart */}
        <div className="col-span-12">
          <Card
            title={`${metrics.find(m => m.id === selectedMetric)?.label} Trend Overlay`}
            subtitle="All selected assets overlaid on single chart"
          >
            {overlayData.length > 0 ? (
              <TimeSeriesChart
                data={overlayData}
                height={350}
                yAxisTitle={selectedMetric === 'anomaly_score' ? 'Score' : 'Percentage'}
                showLegend={true}
              />
            ) : (
              <div className="flex items-center justify-center h-64 text-surface-400">
                No trend data available. Click "Apply Filters" to load data.
              </div>
            )}
          </Card>
        </div>

        {/* Statistics Summary */}
        {summaryStats && (
          <div className="col-span-12 lg:col-span-4">
            <Card title="Comparison Summary" subtitle="Range analysis">
              <div className="space-y-4">
                <div className="p-3 bg-surface-900 rounded-lg">
                  <h4 className="text-sm font-medium text-surface-300 mb-2">Reliability Range</h4>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-surface-400">Min</span>
                    <span className="font-mono text-status-warning">{summaryStats.reliabilityRange.min}%</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-surface-400">Max</span>
                    <span className="font-mono text-status-healthy">{summaryStats.reliabilityRange.max}%</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-surface-400">Spread</span>
                    <span className="font-mono text-primary-400">{summaryStats.reliabilityRange.spread}%</span>
                  </div>
                </div>

                <div className="p-3 bg-surface-900 rounded-lg">
                  <h4 className="text-sm font-medium text-surface-300 mb-2">Health Score Range</h4>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-surface-400">Min</span>
                    <span className="font-mono text-status-warning">{summaryStats.healthRange.min}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-surface-400">Max</span>
                    <span className="font-mono text-status-healthy">{summaryStats.healthRange.max}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-surface-400">Spread</span>
                    <span className="font-mono text-primary-400">{summaryStats.healthRange.spread}</span>
                  </div>
                </div>

                {/* Best & Worst */}
                <div className="p-3 bg-surface-900 rounded-lg">
                  <h4 className="text-sm font-medium text-surface-300 mb-2">Performance Leaders</h4>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-surface-400">Best</span>
                    <span className="font-mono text-status-healthy">{kpiComparison.best_performer}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-surface-400">Needs Attention</span>
                    <span className="font-mono text-status-critical">{kpiComparison.worst_performer}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Correlation Matrix */}
        <div className="col-span-12 lg:col-span-8">
          <Card title="KPI Correlations" subtitle="Cross-asset metric relationships">
            {correlationHeatmap ? (
              <HeatmapChart
                data={correlationHeatmap.data}
                xLabels={correlationHeatmap.labels}
                yLabels={correlationHeatmap.labels}
                height={300}
                colorScale="RdYlGn"
              />
            ) : (
              <div className="flex items-center justify-center h-64 text-surface-400">
                Correlation data will appear after loading comparison data
              </div>
            )}
          </Card>
        </div>

        {/* Asset Legend */}
        <div className="col-span-12">
          <div className="flex flex-wrap items-center gap-4 p-3 bg-surface-800 rounded-lg">
            <span className="text-sm text-surface-400">Legend:</span>
            {kpiComparison.assets.map((asset, index) => (
              <div key={asset.asset_id} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: ASSET_COLORS[index % ASSET_COLORS.length] }}
                />
                <span className="text-sm font-mono text-surface-300">{asset.asset_id}</span>
                <span className={clsx(
                  'text-xs px-1.5 py-0.5 rounded',
                  asset.status === 'healthy' && 'bg-status-healthy/20 text-status-healthy',
                  asset.status === 'warning' && 'bg-status-warning/20 text-status-warning',
                  asset.status === 'critical' && 'bg-status-critical/20 text-status-critical'
                )}>
                  {asset.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
